<template>
  <div
    :class="{
      mobile: maxDevice('lg'),
      desktop: !maxDevice('lg'),
    }"
  >
    <tml-tray
      v-if="maxDevice('lg')"
      name="studio-tray"
      :base-height="trayBaseHeight"
      @leave="activeTab = null"
      @before-enter="onTransitionBeforeEnter"
      @after-enter="onTransitionAfterEnter"
      @before-leave="onTransitionBeforeLeave"
      @after-leave="onTransitionAfterLeave"
    >
      <template v-if="trayTitle" #header>
        <h4>
          <b>{{ trayTitle }}</b>
        </h4>
      </template>
      <template #default>
        <div class="relative h-full">
          <component
            :is="getActiveComponent()"
            ref="child"
            class="flex flex-col"
          />
        </div>
      </template>
    </tml-tray>
    <transition
      v-else
      name="popup-slide-in"
      @before-enter="onTransitionBeforeEnter"
      @after-enter="onTransitionAfterEnter"
      @before-leave="onTransitionBeforeLeave"
      @after-leave="onTransitionAfterLeave"
    >
      <component :is="getActiveComponent()" ref="child" class="flex flex-col" />
    </transition>
    <product-list
      v-if="forceProducts && !getVuexProperty('sku')"
      class="hidden"
    />
    <posta-templates
      v-if="forceTemplates"
      class="hidden"
      :is-internal-user="isInternalUser"
    />
  </div>
</template>

<script>
import {overlays} from '@teemill/modules/overlays';

import {TmlTray} from '@teemill/components';

import {maxDevice} from '@teemill/common/helpers';

export default {
  name: 'StudioPopupSlide',
  components: {
    ProductList: () => import('./../slides/ProductList.vue'),
    DesignList: () => import('./../slides/DesignList.vue'),
    SavePopup: () => import('./../slides/SavePopup.vue'),
    BuyPopup: () => import('./../slides/BuyPopup.vue'),
    SaveTemplatePopup: () => import('./../slides/SaveTemplatePopup.vue'),
    TextEditor: () => import('./../slides/TextEditor.vue'),
    DownloadAsFile: () => import('./../slides/DownloadAsFile.vue'),
    SaveTemplateWithMetaContent: () =>
      import('./../slides/SaveTemplateWithMetaContent.vue'),
    PostaTemplates: () => import('./../slides/PostaTemplates.vue'),
    DriveTab: () => import('./../slides/DriveTab.vue'),
    StickersDesignList: () => import('./../slides/StickersDesignList.vue'),
    TmlTray,
  },

  inject: [
    'getStudioComponentHeight',
    'logger',
    'getVuexProperty',
    'setVuexProperty',
    'getCanvas',
  ],

  provide() {
    return {
      closeAllSlides: this.closeAllSlides,
    };
  },

  props: {
    forceProducts: Boolean,
    forceTemplates: Boolean,
    isInternalUser: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      transitionInterval: null,
      transitionCanvasAnimationRate: 100,

      componentTitles: {
        'design-list': 'Designs',
        'product-list': 'Products',
        'text-editor': 'Text',
        'save-popup': 'Save',
        'save-template-popup': 'Save Template',
        'download-as-file': 'Download',
        'posta-templates': 'Templates',
        'drive-tab': 'Images',
        'stickers-design-list': 'Stickers',
      },

      overlays,
    };
  },

  computed: {
    browserSupportsHistoryApi() {
      return window.history.back.toString().includes('[native code]');
    },

    activeTab: {
      get() {
        return this.getVuexProperty('activeTab');
      },
      set(value) {
        this.setVuexProperty('activeTab', value);
      },
    },

    trayTitle() {
      const componentName = this.activeTab;

      return this.componentTitles[componentName]
        ? this.componentTitles[componentName]
        : null;
    },

    trayBaseHeight() {
      const componentName = this.activeTab;

      if (['save-popup', 'buy-popup'].indexOf(componentName) !== -1) {
        return 90;
      }

      return 65;
    },
  },
  watch: {
    async activeTab(to, from) {
      const queryParams = this.$route.query;
      if (this.browserSupportsHistoryApi) {
        if (to) {
          const newQueryParams = {
            ...queryParams,
            stage: to,
          };

          if (queryParams.stage !== to) {
            if (!from) {
              await this.$router.push({query: newQueryParams});
              history.replaceState(history.state, 'Studio');
            } else {
              history.replaceState(history.state, 'Studio', `?stage=${to}`);
            }
          } else if (!from) {
            await this.$router.push({query: newQueryParams});
            history.replaceState(history.state, 'Studio');
          }
        } else if (queryParams.stage === from) {
          if (history.state?.back === this.$router.currentRoute.value.path) {
            history.back();
          } else {
            await this.$router.replace({
              query: {...queryParams, stage: undefined},
            });
            history.replaceState(history.state, 'Studio');
          }
        }
      }

      if (maxDevice('lg')) {
        if (to) {
          overlays.open('studio-tray');
        } else {
          overlays.close('studio-tray');
        }
      }
    },
  },
  mounted() {
    window.addEventListener('popstate', this.onBeforeBack, false);
  },
  beforeUnmount() {
    window.removeEventListener('popstate', this.onBeforeBack);
    this.endSmoothTransition();
    this.activeTab = 'div';
  },

  methods: {
    maxDevice,
    /**
     * Returns the name of the currently opened slide
     *
     * @return {String}
     */
    getActiveComponent() {
      return this.getVuexProperty('activeTab');
    },

    /**
     * Close all active popup slides
     *
     * @return {void}
     */
    closeAllSlides() {
      this.setVuexProperty('activeTab', false);
    },

    /**
     * Event hook for before the slide in animation is processed
     *
     * @return {void}
     */
    onTransitionBeforeEnter() {
      if (this.getVuexProperty('activeTab')) {
        this.setVuexProperty('slideOpen', true);
      }
      this.startSmoothTransition();
    },

    /**
     * Event hook for after the slide in animation is processed
     *
     * @return {void}
     */
    onTransitionAfterEnter() {
      const child = this.$refs.child;
      if (child && typeof child.afterEnter === 'function') {
        child.afterEnter();
      }

      this.endSmoothTransition();
    },

    /**
     * Event hook for before the slide out animation is processed
     *
     * @return {void}
     */
    onTransitionBeforeLeave() {
      this.setVuexProperty('slideClosing', true);
      this.startSmoothTransition();
    },

    /**
     * Event hook for after the slide out animation is processed
     *
     * @return {void}
     */
    onTransitionAfterLeave() {
      if (!this.getVuexProperty('activeTab')) {
        this.setVuexProperty('slideOpen', false);
      }
      this.setVuexProperty('slideClosing', false);

      if (!this.getCanvas()) {
        return;
      }

      const objects = this.getCanvas().getObjects();
      for (let o = 0, l = objects.length; o < l; ++o) {
        const obj = objects[o];
        if (obj.className === 'Image') {
          if (!obj.colourIndex) {
            obj.getImageMetaData();
          }
        }
      }

      this.endSmoothTransition();
    },

    /**
     * When transitioning between closed and open, the bounds and the background
     * image have to be shuffled around a bit to make sure they overlap properly.
     * This function creates a smoother transition so that it is less jarring to
     * the user
     */
    startSmoothTransition() {
      this.clearTransitionInterval();

      this.transitionInterval = setInterval(() => {
        this.getCanvas().renderer.drawBounds();
      }, this.transitionCanvasAnimationRate);
    },

    clearTransitionInterval() {
      this.endSmoothTransition();
    },

    endSmoothTransition() {
      if (this.transitionInterval) {
        clearInterval(this.transitionInterval);
      }
    },

    /**
     * If someone presses back while a slide is open, close it
     *
     * @return {void}
     */
    onBeforeBack() {
      this.closeAllSlides();
    },
  },
};
</script>

<style lang="scss">
.gallery {
  position: absolute;
  top: 0;
  left: 50%;
  right: 0;
  bottom: 0;
  background-color: var(--tml-page-background-color);
  transition: box-shadow 0.5s ease-in-out;
  box-shadow: $weak-shadow;
  overflow: hidden;
}

.gallery {
  .tab-item {
    width: 22%;
  }
}
.gallery.screen {
  position: fixed;
  top: 68px;
}

.gallery.in {
  left: 100%;
  right: -100%;
}

.gallery-tile {
  display: inline-block;
  vertical-align: top;
  overflow: hidden;
  position: relative;

  &.thin-border {
    border: 1px solid white;
  }

  > span {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: white;
    text-align: center;
    opacity: 0.85;
    color: $text-color;
    pointer-events: none;
  }

  &.art-tile {
    height: 0;
    padding-bottom: 14.2%;
    position: relative;
    border: 2px solid white;

    background-color: #ffffff;
    border-color: var(--tml-page-background-color);
  }

  &.art-tile {
    .tml-image {
      transform: translate(-55%, -55%);
      position: absolute;
      top: 55%;
      left: 55%;
      max-height: 90%;
      max-width: 90%;
      /* -webkit-filter: drop-shadow(2px 2px 2px rgba(0,0,0,0.5));
      -ms-filter: "progid:DXImageTransform.Microsoft.Dropshadow(OffX=2, OffY=2, Color='#444')";
      filter: drop-shadow(2px 2px 2px rgba(0,0,0,0.5)); */
    }
  }
}

.gallery-nav {
  width: 25%;
  display: inline-block;
  border: 1px solid white;
  border-width: 0 1px 0 0;
  border-radius: $border-radius $border-radius 0 0;
  text-align: center;
  font-weight: bold;
  padding: 8px 0;
  vertical-align: top;
  color: #323232;
  transition: border-color 0.25s ease-in-out, opacity 0.2s linear;
  line-height: 2;
  font-size: 16px;
  position: relative;
}

.gallery-nav .bottom-border {
  display: none;
  height: 3px;
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
}

.gallery-nav.active .bottom-border {
  display: block;
}

.gallery-nav:first-of-type {
  border-left-width: 1px;
}

.gallery-nav.inactive {
  pointer-events: none;
}

.gallery-list {
  padding: 0 8px;
  max-height: 100%;

  &.scrollable {
    overflow-y: auto;
  }

  &.design-list {
    max-height: calc(100% - 52px);
  }

  &.checkout {
    max-height: 100%;
  }

  &.upload label {
    font-size: 9px;
    line-height: 1.2;
    margin-left: 28px;
    margin-top: 8px;
  }
}

.gallery-item-title {
  font-size: 12px;
  color: #323232;
}

.gallery-segment-title {
  margin: 0;
  margin-top: 10px;
  line-height: 1;
}

.gallery-segment-subtitle {
  opacity: 0.7;
  margin-bottom: 10px;
}

.gallery .close {
  background-color: black;
  width: 20px;
  height: 20px;
  color: white;
  border-radius: 100%;
  text-align: center;
  vertical-align: middle;
  font-weight: lighter;
  opacity: 1;
  padding: 3px;
  position: absolute;
  top: -10px;
  font-size: 90%;
  right: 0px;
  z-index: 10;
}

@media (max-width: 991px) {
  .gallery-list {
    padding-right: 0.5rem !important;
    padding-left: 0;
  }

  .gallery-tile.art-tile {
    padding-bottom: 24%;
  }

  .gallery {
    top: 0;
    width: 100%;
    left: 0;
    right: auto;
    z-index: $studio-save-drawer-z-index;
  }

  .gallery.in {
    animation: none;
    top: 102%;
    bottom: -100%;
    left: 0;
    right: auto;
  }
}

.popup-slide-in-enter-active,
.popup-slide-in-leave-active {
  transition: all 0.3s ease;

  .gallery {
    transition: all 0.3s ease;
  }
  /* z-index: -1; */
}
.popup-slide-in-enter-from, .popup-slide-in-leave-to
/* .slide-fade-leave-active below version 2.1.8 */ {
  &.gallery {
    transform: translateX(100%);
  }
  @media (min-width: 992px) {
    .gallery {
      left: 100%;
    }
  }
}

@media (max-width: 991px) {
  .popup-slide-in-enter-from, .popup-slide-in-leave-to
  /* .slide-fade-leave-active below version 2.1.8 */ {
    &.gallery {
      transform: translateY(100%);
    }
    .gallery {
      top: 100%;
    }
  }
}

@media (max-width: 767px) and (min-aspect-ratio: 5/6) and (orientation: landscape) {
  .gallery {
    bottom: 0;
    top: 58px;
  }
}
</style>
